import { PlanetIcon } from '@phosphor-icons/react';
import {
  Desktop,
  DeviceMobile,
  RM1,
  RM2,
  RMpp,
  RMppMove,
} from '@remarkable/ark-icons';

import googleChromeLogo from 'src/assets/icons/googleChrome.svg';
import msPowerPoint from 'src/assets/icons/msPowerPoint.svg';
import msWord from 'src/assets/icons/msWord.svg';
import type { AnyDevice, DeviceType } from 'src/utils/deviceHelpers';
import { deviceHelpers } from 'src/utils/deviceHelpers';

export const DeviceIcon = (props: {
  device: AnyDevice | undefined;
  deviceType?: DeviceType;
  size?: number;
  className?: string;
  id?: string;
}) => {
  const size = props.size ?? 24;

  const deviceType = props.device
    ? deviceHelpers.getDeviceType(props.device)
    : (props.deviceType ?? 'UNKNOWN');
  const id = props.id ?? `device-icon-${deviceType}`;

  switch (deviceType) {
    case 'RM1':
      return (
        <RM1
          size={size}
          className={props.className}
          data-cy="RM1-icon"
          aria-label="reMarkable 1 device icon"
          id={id}
        />
      );
    case 'RM2':
      return (
        <RM2
          size={size}
          className={props.className}
          data-cy="RM2-icon"
          aria-label="reMarkable 2 device icon"
          id={id}
        />
      );
    case 'RMPP':
      return (
        <RMpp
          size={size}
          className={props.className}
          data-cy="RMPP-icon"
          aria-label="reMarkable Paper Pro device icon"
          id={id}
        />
      );
    case 'RMPPMove':
      return (
        <RMppMove
          size={size}
          className={props.className}
          data-cy="RMPPMove-icon"
          aria-label="reMarkable Paper Pro Move device icon"
          id={id}
        />
      );
    case 'RMPPure':
      return (
        <RM2
          size={size}
          className={props.className}
          data-cy="RMPPure-icon"
          aria-label="reMarkable Paper Pure device icon"
          id={id}
        />
      );
    case 'RM_UNKNOWN':
      return (
        <RM2
          size={size}
          className={props.className}
          data-cy="RM-unknown-icon"
          aria-label="reMarkable unknown device icon"
          id={id}
        />
      );
    case 'DESKTOP_LINUX':
    case 'DESKTOP_MACOS':
    case 'DESKTOP_WINDOWS':
    case 'DESKTOP_UNKNOWN':
      return (
        <Desktop
          size={size}
          className={props.className}
          data-cy="desktop-icon"
          aria-label="Desktop device icon"
          id={id}
        />
      );
    case 'MOBILE_ANDROID':
    case 'MOBILE_IOS':
    case 'MOBILE_UNKNOWN':
      return (
        <DeviceMobile
          size={size}
          className={props.className}
          data-cy="mobile-icon"
          aria-label="Mobile device icon"
          id={id}
        />
      );
    case 'WORD_EXTENSION':
      return (
        <img
          className={props.className}
          height={size}
          width={size}
          src={msWord}
          alt="Word extension icon"
          data-cy="word-extension-icon"
          id={id}
        ></img>
      );
    case 'POWERPOINT_EXTENSION':
      return (
        <img
          className={props.className}
          height={size}
          width={size}
          src={msPowerPoint}
          alt="PowerPoint extension icon"
          data-cy="powerpoint-extension-icon"
          id={id}
        ></img>
      );
    case 'BROWSER_CHROME':
      return (
        <img
          id={id}
          className={props.className}
          src={googleChromeLogo}
          alt="Google Chrome browser extension icon"
          width={size}
          height={size}
          data-cy="browser-chrome-icon"
        />
      );
    case 'BROWSER_UNKNOWN':
      return (
        <PlanetIcon
          size={size}
          className={props.className}
          data-cy="browser-unknown-icon"
          aria-label="Unknown browser extension icon"
          id={id}
        />
      );
    case 'UNKNOWN':
    default:
      return (
        <PlanetIcon
          size={size}
          className={props.className}
          data-cy="unknown-device-icon"
          aria-label="Unknown device icon"
          id={id}
        />
      );
  }
};
